import type { Category, ScoringSex } from '../scoring/types';
import type { MonthlyBest, MonthlySummary } from '../workouts/types';
import { CATEGORY_LABELS, COMPETITION_LIFT_NAMES } from '../scoring/categories';

/** A single user's row on the leaderboard */
export interface LeaderboardEntry {
  userId: string;
  displayName: string;
  monthKey: string;
  /** Drives the gender filter. Taken from the profile, not from any one lift. */
  scoringSex: ScoringSex;

  // Points mode
  pushPoints: number;
  rowPoints: number;
  hingePoints: number;
  squatPoints: number;
  totalPoints: number;

  // Max 1RM mode
  pushNormalized1RmLb: number;
  rowNormalized1RmLb: number;
  hingeNormalized1RmLb: number;
  squatNormalized1RmLb: number;
  combinedNormalized1RmLb: number;

  /** The best lift of each category this month, for the board's sub-text. */
  bests: Partial<Record<Category, MonthlyBest>>;
  /** The best lift of each individual exercise, keyed by exercise id. */
  exerciseBests: Record<string, MonthlyBest>;

  isIncomplete: boolean;
  rank?: number;
  isCurrentUser?: boolean;
}

export type LeaderboardMode = 'points' | 'max1rm';

/**
 * What the board is ranking.
 *
 * - `total`: the sum across all four categories, the default board.
 * - `category`: one category, ranked on its best lift - whichever exercise that
 *   was - so a bench and an overhead press compete on normalised terms.
 * - `exercise`: one specific lift. Everyone here did the same movement, so the
 *   ranking is on that lift's own 1RM with no normalising involved.
 */
export type LeaderboardScope =
  | { kind: 'total' }
  | { kind: 'category'; category: Category }
  | { kind: 'exercise'; exerciseId: string };

export const TOTAL_SCOPE: LeaderboardScope = { kind: 'total' };

/** The gender filter. 'all' leaves the board untouched. */
export type SexFilter = 'all' | ScoringSex;

/** Narrows a board to one scoring sex. Ranking happens after this, so ranks are
 *  always 1..n within what is actually displayed rather than gappy. */
export function filterBySex(entries: LeaderboardEntry[], filter: SexFilter): LeaderboardEntry[] {
  return filter === 'all' ? entries : entries.filter((e) => e.scoringSex === filter);
}

/** The lift a scoped board is ranking on, or undefined if the user has none. */
export function scopedBest(
  entry: LeaderboardEntry,
  scope: LeaderboardScope,
): MonthlyBest | undefined {
  if (scope.kind === 'category') return entry.bests[scope.category];
  if (scope.kind === 'exercise') return entry.exerciseBests[scope.exerciseId];
  return undefined;
}

/** The number a scoped board sorts and displays. */
export function scopedValue(
  entry: LeaderboardEntry,
  mode: LeaderboardMode,
  scope: LeaderboardScope,
): number {
  if (scope.kind === 'total') {
    return mode === 'points' ? entry.totalPoints : entry.combinedNormalized1RmLb;
  }
  const best = scopedBest(entry, scope);
  if (!best) return 0;
  if (mode === 'points') return best.points;
  // A single exercise ranks on its own 1RM - everyone is doing the same movement,
  // so normalising to the competition lift would only scale every row equally and
  // show a number nobody actually lifted.
  return scope.kind === 'exercise' ? best.estimated1RmLb : best.normalized1RmLb;
}

/**
 * Sort and rank leaderboard entries. Uses standard competition ranking
 * (ties share rank: 1, 2, 2, 4).
 *
 * Scoped boards drop anyone without a qualifying lift: a bench press board
 * listing people who never benched, all on zero, is noise rather than a ranking.
 * The total board keeps everyone and flags them incomplete instead.
 */
export function buildLeaderboard(
  entries: LeaderboardEntry[],
  mode: LeaderboardMode = 'points',
  scope: LeaderboardScope = TOTAL_SCOPE,
): LeaderboardEntry[] {
  const eligible =
    scope.kind === 'total' ? entries : entries.filter((e) => scopedBest(e, scope) !== undefined);

  const valueOf = (e: LeaderboardEntry) => scopedValue(e, mode, scope);

  const sorted = [...eligible].sort((a, b) => {
    const diff = valueOf(b) - valueOf(a);
    if (diff !== 0) return diff;
    // Tiebreaker: alphabetical by name
    return a.displayName.localeCompare(b.displayName);
  });

  // Standard competition ranking: ties share rank, next rank skips
  let currentRank = 1;
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && valueOf(sorted[i]) < valueOf(sorted[i - 1])) {
      currentRank = i + 1;
    }
    sorted[i] = { ...sorted[i], rank: currentRank };
  }

  return sorted;
}

/**
 * Convert a MonthlySummary into a partial LeaderboardEntry.
 */
export function summaryToLeaderboardEntry(
  userId: string,
  displayName: string,
  monthKey: string,
  summary: MonthlySummary,
  exerciseBests: Record<string, MonthlyBest> = {},
  scoringSex: ScoringSex = 'male',
): Omit<LeaderboardEntry, 'rank' | 'isCurrentUser'> {
  return {
    userId,
    displayName,
    monthKey,
    scoringSex,
    bests: {
      ...(summary.pushBest ? { push: summary.pushBest } : {}),
      ...(summary.rowBest ? { row: summary.rowBest } : {}),
      ...(summary.hingeBest ? { hinge: summary.hingeBest } : {}),
      ...(summary.squatBest ? { squat: summary.squatBest } : {}),
    },
    exerciseBests,
    pushPoints: summary.pushBest?.points ?? 0,
    rowPoints: summary.rowBest?.points ?? 0,
    hingePoints: summary.hingeBest?.points ?? 0,
    squatPoints: summary.squatBest?.points ?? 0,
    totalPoints: summary.totalPoints,
    pushNormalized1RmLb: summary.pushBest?.normalized1RmLb ?? 0,
    rowNormalized1RmLb: summary.rowBest?.normalized1RmLb ?? 0,
    hingeNormalized1RmLb: summary.hingeBest?.normalized1RmLb ?? 0,
    squatNormalized1RmLb: summary.squatBest?.normalized1RmLb ?? 0,
    combinedNormalized1RmLb:
      (summary.pushBest?.normalized1RmLb ?? 0) +
      (summary.rowBest?.normalized1RmLb ?? 0) +
      (summary.hingeBest?.normalized1RmLb ?? 0) +
      (summary.squatBest?.normalized1RmLb ?? 0),
    isIncomplete: summary.isIncomplete,
  };
}

/** What a board is ranking, for the caption under the toggles. */
export function getScopeLabel(
  scope: LeaderboardScope,
  mode: LeaderboardMode,
  exerciseName?: string,
): string {
  if (scope.kind === 'total') {
    return mode === 'points' ? 'Total points' : 'Combined 1RM';
  }
  if (scope.kind === 'exercise') {
    return mode === 'points' ? `${exerciseName ?? 'Lift'} points` : `${exerciseName ?? 'Lift'} 1RM`;
  }
  const category = CATEGORY_LABELS[scope.category];
  return mode === 'points'
    ? `${category} points`
    : `${category} · ${COMPETITION_LIFT_NAMES[scope.category]} equivalent`;
}

/**
 * Format a month key (YYYY-MM) to a human-readable string.
 */
export function formatMonthKey(monthKey: string): string {
  const [yearStr, monthStr] = monthKey.split('-');
  const date = new Date(parseInt(yearStr), parseInt(monthStr) - 1);
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

/**
 * Get the previous month key from a given month key.
 */
export function getPreviousMonthKey(monthKey: string): string {
  const [yearStr, monthStr] = monthKey.split('-');
  const date = new Date(parseInt(yearStr), parseInt(monthStr) - 1 - 1);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}

/**
 * Get the next month key from a given month key.
 */
export function getNextMonthKey(monthKey: string): string {
  const [yearStr, monthStr] = monthKey.split('-');
  const date = new Date(parseInt(yearStr), parseInt(monthStr) - 1 + 1);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}
